import type { ReactElement } from 'react';

import { MarketplacePropertyCategory_ListingMediaState as ListingMediaState } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import type { MarketplaceProperty } from 'components/marketplace/property/types';

import { HeroWalkthroughVideo } from './HeroWalkthroughVideo';
import type { ListingMediaAvailability } from './mediaSlot';
import { NO_ENHANCED_MEDIA } from './mediaSlot';
import type { PdpMediaSlot } from './usePdpMediaSlot';

/**
 * Reel to play in the listing's hero, or null when it has none.
 *
 * Reels are ordered newest first and bound to the listing rather than the
 * address, so a relist never resurfaces a previous ownership cycle's footage.
 */
export const heroWalkthroughVideoUrl = (
  property: Pick<MarketplaceProperty, 'walkthroughVideos'>,
): string | null => property.walkthroughVideos[0]?.url ?? null;

/**
 * Availability is derived from the URL, not reported beside it, so a listing
 * cannot enroll in the video test with nothing to play and credit the video
 * arm with a photo viewer's behaviour.
 */
export const pdpMediaAvailability = (videoUrl: string | null): ListingMediaAvailability => ({
  ...NO_ENHANCED_MEDIA,
  // Truthiness, matching the renderer's own gate: an empty URL would otherwise
  // enroll a listing that paints no video.
  hasWalkthroughVideo: Boolean(videoUrl),
});

/**
 * Whether the hero overlay should be mounted at all.
 *
 * The gallery surrenders the hero tile's click to whatever it is handed, and
 * cannot see that the overlay renders nothing for a control-arm viewer. Mounting
 * on the URL alone therefore strips that tile's lightbox click with nothing put
 * in its place, depressing the arm the readout compares against.
 */
export const shouldMountHeroVideo = (
  videoUrl: string | null,
  assignedState: ListingMediaState,
): boolean => Boolean(videoUrl) && assignedState === ListingMediaState.VIDEO;

/** Overlay the mini-PDP carousel renders over its first slide, or undefined when nothing should mount. */
export const miniPdpHeroVideo = (
  property: Pick<
    MarketplaceProperty,
    'walkthroughVideos' | 'addressToken' | 'street' | 'unitType' | 'unitNumber'
  >,
  slot: PdpMediaSlot,
  // The desktop drawer is a half-viewport panel, wider than the mobile
  // bottom sheet the 480px control default serves.
  reelWidth?: number,
): ReactElement | undefined => {
  const videoUrl = heroWalkthroughVideoUrl(property);
  if (!shouldMountHeroVideo(videoUrl, slot.assignedState) || !videoUrl) {
    return undefined;
  }
  return (
    <HeroWalkthroughVideo
      key={property.addressToken}
      videoUrl={videoUrl}
      addressToken={property.addressToken}
      addressLabel={formatStreetAddress(property.street, property.unitType, property.unitNumber)}
      slot={slot}
      trigger="control"
      reelWidth={reelWidth}
    />
  );
};
